import React from 'react';
import { Link, useLocation } from "react-router-dom";
import { Home, ChevronRight } from "lucide-react";
import { Helmet } from 'react-helmet-async';
import { generateBreadcrumbItems } from "@/utils/seoUtils";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface BreadcrumbsWithSchemaProps {
  items?: BreadcrumbItem[];
  customLabels?: Record<string, string>;
  className?: string;
}

/**
 * BreadcrumbsWithSchema - Renders breadcrumb navigation with schema.org markup
 */
const BreadcrumbsWithSchema: React.FC<BreadcrumbsWithSchemaProps> = ({ 
  items = [],
  customLabels = {},
  className = "" 
}) => {
  const location = useLocation();
  
  // Build breadcrumb items if not provided
  const breadcrumbItems: BreadcrumbItem[] = items.length > 0 ? items : buildBreadcrumbFromPath(location.pathname, customLabels);
  
  // Generate schema.org compatible breadcrumb items
  const schemaItems = breadcrumbItems.map((item, index) => ({
    name: item.label,
    url: item.path || `${window.location.origin}${location.pathname.split('/').slice(0, index + 2).join('/')}`
  }));
  
  // Add home page if not already included
  if (schemaItems.length > 0 && !schemaItems[0].url.endsWith('/')) {
    schemaItems.unshift({ name: 'Inicio', url: window.location.origin });
  }

  // Schema JSON for breadcrumbs
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": schemaItems.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      </Helmet>
      
      <div className={`py-2 ${className}`}>
        <div className="container px-4">
          <Breadcrumb>
            <BreadcrumbList className="flex-wrap justify-start gap-1 text-xs sm:text-sm">
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link 
                    to="/" 
                    className="flex items-center text-muted-foreground/70 hover:text-foreground transition-colors"
                    aria-label="Inicio"
                  >
                    <Home className="h-3.5 w-3.5 shrink-0" />
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              
              {breadcrumbItems.map((item, index) => (
                <span key={index} className="contents">
                  <BreadcrumbSeparator className="text-muted-foreground/40">
                    <ChevronRight className="h-3 w-3 shrink-0" />
                  </BreadcrumbSeparator>
                  <BreadcrumbItem>
                    {item.path && index !== breadcrumbItems.length - 1 ? (
                      <BreadcrumbLink asChild>
                        <Link 
                          to={item.path} 
                          className="text-muted-foreground/70 hover:text-foreground transition-colors truncate max-w-[120px] sm:max-w-[180px]"
                        >
                          {item.label}
                        </Link>
                      </BreadcrumbLink>
                    ) : (
                      <BreadcrumbPage className="text-foreground/80 font-medium truncate max-w-[150px] sm:max-w-[220px]">
                        {item.label}
                      </BreadcrumbPage>
                    )}
                  </BreadcrumbItem>
                </span>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>
    </>
  );
};
// Helper function to build breadcrumb items from path
function buildBreadcrumbFromPath(path: string, customLabels: Record<string, string>): BreadcrumbItem[] {
  // Remove trailing slash
  const cleanPath = path.endsWith("/") ? path.slice(0, -1) : path;
  
  if (cleanPath === "") return [];
  
  const segments = cleanPath.split("/").filter(Boolean);
  const breadcrumbItems: BreadcrumbItem[] = [];
  
  let currentPath = "";
  
  for (let i = 0; i < segments.length; i++) {
    currentPath += `/${segments[i]}`;
    
    const label = formatBreadcrumbLabel(segments[i], currentPath, segments, i, customLabels);
    
    breadcrumbItems.push({
      label,
      path: i === segments.length - 1 ? undefined : currentPath,
    });
  }
  
  return breadcrumbItems;
}

// Helper function to format path segments into readable labels
function formatBreadcrumbLabel(
  pathSegment: string, 
  currentPath: string, 
  segments: string[], 
  index: number, 
  customLabels: Record<string, string>
): string {
  // First check for full path match in custom labels
  if (customLabels[currentPath]) {
    return customLabels[currentPath];
  }
  
  // Then check for segment-specific label
  if (customLabels[pathSegment]) {
    return customLabels[pathSegment];
  }
  
  // Handle special cases with new URL structure
  const specialCases: Record<string, string> = {
    // Secciones principales
    "quienes-somos": "Quiénes Somos",
    "servicios": "Servicios",
    "casos-exito": "Casos de Éxito",
    "blog": "Blog",
    "recursos": "Recursos",
    "faq": "Preguntas Frecuentes",
    "contacto": "Contacto",
    
    // Quienes Somos subsecciones
    "mision-vision": "Misión y Visión",
    "valores": "Nuestros Valores",
    "equipo": "Nuestro Equipo",
    "carreras": "Trabaja con Nosotros",
    
    // Servicios - Categorías
    "gestoria-administracion": "Gestoría y Administración",
    "asesoria-legal": "Asesoría Legal",
    "cursos-idiomas": "Cursos de Idiomas",
    "traducciones-oficiales": "Traducciones Oficiales",
    "diseno-web-hosting": "Diseño Web y Hosting",
    "consultoria-seguros": "Consultoría de Seguros",
    "soluciones-digitales-avanzadas": "Soluciones Digitales Avanzadas",
    
    // Servicios - Gestoría subcategorías
    "creacion-empresas-suiza": "Creación de Empresas en Suiza",
    "planificacion-fiscal": "Planificación Fiscal",
    "tramites-laborales-sociales": "Trámites Laborales y Sociales",
    "gestiones-menores": "Gestiones Menores",
    
    // Servicios - Asesoría Legal subcategorías
    "reagrupacion-familiar-visados": "Reagrupación Familiar y Visados",
    "derecho-laboral-contratos": "Derecho Laboral y Contratos",
    "derecho-familia-sucesiones": "Derecho de Familia y Sucesiones",
    "legalizaciones-apostillas": "Legalizaciones y Apostillas",
    
    // Servicios - Cursos de Idiomas subcategorías
    "aleman-a1-c1": "Alemán (A1-C1)",
    "ingles-b1-c1": "Inglés (B1-C1)",
    "presencial": "Cursos Presenciales",
    "online": "Cursos Online",
    "clase-prueba": "Clase de Prueba",
    
    // Servicios - Traducciones subcategorías
    "documentos-legales": "Documentos Legales",
    "interprete-24h": "Intérprete 24h",
    "revision-terminologica": "Revisión Terminológica",
    
    // Servicios - Diseño Web subcategorías
    "web-esencial-3-pags": "Web Esencial (3 páginas)",
    "web-profesional-5-7-pags": "Web Profesional (5-7 páginas)",
    "e-commerce-basico": "E-commerce Básico",
    "mantenimiento-basico": "Mantenimiento Básico",
    "mantenimiento-premium": "Mantenimiento Premium",
    
    // Servicios - Seguros subcategorías
    "seguros-salud": "Seguros de Salud",
    "seguros-hogar-vehiculos": "Seguros de Hogar y Vehículos",
    "seguros-vida-pension": "Seguros de Vida y Pensión",
    "seguros-empresariales": "Seguros Empresariales",
    
    // Recursos subsecciones
    "whitepapers": "Whitepapers",
    "guias-practicas": "Guías Prácticas",
    "videos-webinars": "Videos y Webinars",
    
    // Contacto subsecciones
    "formulario": "Formulario de Contacto",
    "oficina-opfikon": "Oficina de Opfikon",
    "whatsapp-chat": "WhatsApp y Chat",
    
    // Legal
    "politica-privacidad": "Política de Privacidad",
    "aviso-legal": "Aviso Legal",
    "cookies": "Política de Cookies",
    
    // Dashboard
    "dashboard": "Dashboard",
    "inicio": "Inicio",
    "perfil": "Mi Perfil",
    "avatar": "Foto de Perfil",
    "datos-personales": "Datos Personales",
    "cambio-password": "Cambiar Contraseña",
    "documentos": "Documentos",
    "subir": "Subir Documento",
    "historial": "Historial",
    "facturas": "Facturas",
    "pendientes": "Pendientes",
    "citas": "Citas", 
    "nueva": "Nueva Cita",
    "proximas": "Próximas Citas",
    "mensajes": "Mensajes",
    "entrada": "Bandeja de Entrada",
    "enviados": "Mensajes Enviados",
    "soporte": "Soporte",
    "tickets": "Mis Tickets",
    "nuevo-ticket": "Nuevo Ticket",
    "integraciones": "Integraciones",
    "chatbot": "Chatbot",
    "whatsapp": "WhatsApp",
    "voicebot": "Voicebot",
    "alexa-skill": "Alexa Skill",
    "resumen": "Resumen",
    "configuracion": "Configuración",
    "entrenamiento": "Entrenamiento",
    "conversaciones": "Conversaciones",
    "analitica": "Analítica",
    "plantillas": "Plantillas",
    "llamadas": "Llamadas",
    
    // Auth
    "login": "Iniciar Sesión",
    "registro": "Registrarse",
    "confirmacion-cuenta": "Confirmar Cuenta",
    "password-reset": "Restablecer Contraseña",
    
    // Legacy URL mappings
    "gestoria-fiscal": "Gestoría y Fiscal",
    "seguros-pensiones": "Seguros y Pensiones",
    "digital": "Soluciones Digitales",
    "legal": "Asesoría Legal",
    "idiomas": "Formación en Idiomas",
    "nosotros": "Sobre Nosotros",
    "politica-de-privacidad": "Política de Privacidad",
    "politica-de-cookies": "Política de Cookies"
  };
  
  if (specialCases[pathSegment]) {
    return specialCases[pathSegment];
  }
  
  // Handle special case for blog categories
  if (segments[0] === "blog" && segments[1] === "categoria" && index === 2) {
    return `Categoría: ${formatTitleFromSlug(pathSegment)}`;
  }
  
  // Format camelCase, kebab-case, or snake_case into proper title case
  return formatTitleFromSlug(pathSegment);
}

// Helper to format a slug into a proper title
function formatTitleFromSlug(slug: string): string {
  return slug
    .replace(/-/g, " ")
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export default BreadcrumbsWithSchema;
